import { FileWithPath } from '@mantine/dropzone';
import { AxiosProgressEvent } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  addDatasetsFiles,
  AIModelType,
  createRobotDatasets,
  DatasetsFilesInfo,
  FileStatus,
  getDatasetsFilesList,
} from '@mygpt/app';

import { useCreateStore } from '@/store/create';
import { useGPTStore } from '@/store';
import { isFileStatusProcess, isFileStatusReady } from '@/utils/embed';

interface Task2Rsp {
  id: string;
  filesId: string[];
}

export interface TaskError {
  id: string;
  fileName: string;
  message: string;
}

export interface TaskDocument {
  id: string;
  name: string;
  status: FileStatus;
  file_size: number;
  message?: string;
  tokens?: number;
  words?: number;
}

export const useTaskDocument = (files: FileWithPath[]) => {
  const { t } = useTranslation();
  const listTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { datasetId, embeddingModelName, pageType, setPageType, setBotId, setDatasetId } = useCreateStore();
  const { assetScope } = useGPTStore();
  const isCloudMode = import.meta.env.VITE_MODE !== 'local';
  const [percent, setPercent] = useState<number>(0);
  const [documents, setDocuments] = useState<TaskDocument[]>([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const onUploadProgress = (event: AxiosProgressEvent, index: number) => {
    if (!event.total) return;
    const percent = Math.round((event.loaded * 100) / event.total);
    setPercent(Math.min(percent, 99));
  };

  const task_1 = async (): Promise<string | undefined> => {
    if (pageType === 'create') {
      const fileName = files[0].name;
      const res = await createRobotDatasets({
        aiModel: AIModelType.FILE,
        name: fileName,
        embeddingModelName,
        ...(isCloudMode && assetScope && { companyId: assetScope }),
      });
      if (!res) return;
      const datasets = res.data;
      if (datasets.error) return Promise.reject(datasets.error);
      const botId = queryParams.get('botId');
      setBotId(botId || '');
      const { id: datasetId } = datasets;
      setDatasetId(datasetId);
      setPageType('modify');
      return datasetId;
    } else {
      return datasetId;
    }
  };

  const task_2 = (id: string): Promise<Task2Rsp | undefined> => {
    return new Promise((resolve, reject) => {
      if (!files.length) reject('Failed to get file');

      const uploadErrors: string[] = [];

      // 控制最大并发数
      const maxConcurrency = 5;
      let running = 0;

      const queue: (() => Promise<void>)[] = [];

      const runTask = (task: () => Promise<void>) => {
        return new Promise((resolve, reject) => {
          task()
            .then(resolve)
            .catch(reject)
            .finally(() => {
              running--;
              next();
            });

          running++;
        });
      };
      const filesId: string[] = [];
      const next = () => {
        while (queue.length > 0 && running < maxConcurrency) {
          const task = queue.shift();
          task && runTask(task);
        }

        if (queue.length !== 0 || running !== 0) return;

        if (uploadErrors.length === files.length) {
          reject(uploadErrors[0]);
        }

        resolve({
          id,
          filesId,
        });
      };

      queue.push(async () => {
        // 文件处理逻辑
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append('files', files[i]);
        }
        await addDatasetsFiles({
          id: id,
          formData,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            console.log(progressEvent);
            onUploadProgress(progressEvent, 0);
          },
        })
          .then((res) => {
            if (res.data) {
              const documents = res.data.map((item) => {
                filesId.push(item.id);
                return {
                  id: item.id,
                  name: item.filename,
                  status: item.file_status,
                  file_size: item.file_size,
                };
              });
              setDocuments(documents);
            }
            setPercent(100);
          })
          .catch((err) => {
            uploadErrors.push(err.response?.data?.detail || t('errors.unknown.description'));
          });
      });

      next();
    });
  };

  const task_3 = ({ filesId, id }: Task2Rsp): Promise<boolean | undefined> => {
    return new Promise((resolve, reject) => {
      let chekcListDataTimes = 0;

      const checkFileStatus = () => {
        window.clearTimeout(listTimeoutRef.current);
        // start_time 为当天12小时前的时间
        const start_time = new Date(new Date().getTime() - 12 * 60 * 60 * 1000).toISOString();
        getDatasetsFilesList({ id: id, file_type: 'file', page: 1, size: 999, start_time })
          .then((res) => {
            const { items } = res.data;
            if (!items.length) {
              // 超过5次都没有可能就有问题了
              if (chekcListDataTimes >= 5) {
                reject('Sorry, there was an issue during the document learning process.');
                return;
              }
              // 可能不会那么快有列表数据
              chekcListDataTimes++;
              listTimeoutRef.current = setTimeout(() => checkFileStatus(), 2 * 1000);
              return;
            }

            // 检查列表里是否已有刚上传文件的id
            if (!filesId.every((id) => items.map((item: DatasetsFilesInfo) => item.id).includes(id))) {
              listTimeoutRef.current = setTimeout(() => checkFileStatus(), 2 * 1000);
              return;
            }

            const processArr = items.filter(
              (item: DatasetsFilesInfo) =>
                filesId.indexOf(item.id) > -1 &&
                (isFileStatusProcess(item.file_status) || isFileStatusReady(item.file_status))
            );
            setDocuments((prev) => {
              return prev.map((item) => {
                const currentItem = items.find((i) => i.id === item.id);
                if (currentItem) {
                  item.status = currentItem.file_status;
                  item.message = currentItem.failed_reason;
                  item.tokens = currentItem.token_count;
                  item.words = currentItem.characters_count;
                }
                return item;
              });
            });

            if (processArr.length) {
              listTimeoutRef.current = setTimeout(() => checkFileStatus(), 5 * 1000);
            } else {
              resolve(true);
            }
          })
          .catch(() => {
            listTimeoutRef.current = setTimeout(() => checkFileStatus(), 10 * 1000);
          });
      };
      checkFileStatus();
    });
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(listTimeoutRef.current);
    };
  }, []);

  return {
    percent,
    documents,
    tasks: [task_1, task_2, task_3],
  };
};
